import { useCallback } from "react";
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    pointerWithin,
    rectIntersection,
    useDroppable,
    useSensor,
    useSensors,
    type CollisionDetection,
    type DragEndEvent,
    type DragOverEvent,
    type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useState } from "react";
import { cloneWithNewIds, type DocNode, type EmailDocument } from "@edm/schema";
import { useEditorStore } from "../store/editorStore";
import {
    createFromPalette,
    insertNode,
    moveNode,
    resolveDropTarget,
} from "../docops";
import { NodeRenderer } from "./NodeRenderer";
import { BlockPalette, paletteLabel } from "./BlockPalette";

const isContainerId = (id: string | number) => String(id).startsWith("container:");

/**
 * Prefer the block directly under the pointer over the container that encloses
 * it, so drops land BETWEEN siblings rather than being appended to the end of
 * the parent. Falls back to the innermost container (a column beats the root),
 * then to closestCenter for empty regions.
 */
const collisionDetection: CollisionDetection = (args) => {
    const pointerHits = pointerWithin(args);
    const hits = pointerHits.length ? pointerHits : rectIntersection(args);

    const nodeHits = hits.filter((h) => !isContainerId(h.id));
    if (nodeHits.length) {
        const scoped = closestCenter({
            ...args,
            droppableContainers: args.droppableContainers.filter((c) =>
                nodeHits.some((n) => n.id === c.id),
            ),
        });
        if (scoped.length) return scoped;
    }

    if (hits.length) {
        // Only containers under the pointer — choose the innermost (smallest).
        const areaOf = (id: string | number) => {
            const rect = args.droppableContainers.find((c) => c.id === id)?.rect
                .current;
            return rect ? rect.width * rect.height : Number.POSITIVE_INFINITY;
        };
        return [...hits].sort((a, b) => areaOf(a.id) - areaOf(b.id)).slice(0, 1);
    }

    return closestCenter(args);
};

/** The concrete node type a palette/saved/existing drag will produce. */
function draggedNodeType(a: DragData): string | undefined {
    if (a.kind === "palette" && a.blockType) return createFromPalette(a.blockType).type;
    if (a.kind === "saved") return a.block?.type;
    if (a.kind === "node") return a.nodeType;
    return undefined;
}

function dragLabel(a: DragData): string {
    if (a.kind === "palette") return paletteLabel(a.blockType ?? "");
    if (a.kind === "saved") return a.label ?? "block";
    return a.nodeType ?? "block";
}

export interface DragData {
    kind: "palette" | "node" | "saved";
    blockType?: string;
    nodeId?: string;
    parentId?: string;
    nodeType?: string;
    block?: DocNode;
    label?: string;
}

export function Canvas({ document: doc }: { document: EmailDocument }) {
    const applyDocChange = useEditorStore((s) => s.applyDocChange);
    const select = useEditorStore((s) => s.select);
    const notify = useEditorStore((s) => s.notify);
    const setDropHint = useEditorStore((s) => s.setDropHint);
    const savedBlocksVersion = useEditorStore((s) => s.savedBlocksVersion);
    const [dragging, setDragging] = useState<DragData | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor),
    );

    const onDragStart = useCallback((e: DragStartEvent) => {
        setDragging((e.active.data.current as DragData) ?? null);
    }, []);

    // Live insertion line: reflect where the current drop would land.
    const onDragOver = useCallback(
        (e: DragOverEvent) => {
            const o = e.over?.data.current as
                | (DragData & { containerId?: string })
                | undefined;
            if (!o || !e.over) {
                setDropHint(null);
                return;
            }
            if (o.kind === "node" && o.nodeId) {
                const activeY = e.active.rect.current.translated?.top ?? 0;
                const overRect = e.over.rect;
                const after = activeY > overRect.top + overRect.height / 2;
                setDropHint({ nodeId: o.nodeId, side: after ? "after" : "before" });
            } else {
                // Container highlight (its own `isOver`) covers this case.
                setDropHint(null);
            }
        },
        [setDropHint],
    );

    const endDrag = useCallback(() => {
        setDragging(null);
        setDropHint(null);
    }, [setDropHint]);

    const onDragEnd = useCallback(
        (e: DragEndEvent) => {
            endDrag();
            const a = e.active.data.current as DragData | undefined;
            const o = e.over?.data.current as
                | (DragData & { containerId?: string })
                | undefined;
            if (!a || !e.over || !o) return;

            const type = draggedNodeType(a);
            if (!type) return;

            // Dropping a block exactly onto itself is a no-op, not an error.
            if (a.kind === "node" && a.nodeId && a.nodeId === o.nodeId) return;

            const activeY = e.active.rect.current.translated?.top ?? 0;
            const after =
                o.kind === "node"
                    ? activeY > e.over.rect.top + e.over.rect.height / 2
                    : true; // appended within a container

            const target = resolveDropTarget(
                doc.root,
                type,
                { nodeId: o.kind === "node" ? o.nodeId : undefined, containerId: o.containerId },
                after,
                a.kind !== "node", // only palette/saved inserts auto-wrap on root
            );

            if (!target) {
                notify(
                    `A ${dragLabel(a)} can’t go there — drop it into a column or onto the canvas.`,
                    "error",
                );
                return;
            }

            applyDocChange((draft) => {
                let ok = false;
                if (a.kind === "palette" && a.blockType) {
                    ok = insertNode(
                        draft,
                        createFromPalette(a.blockType),
                        target.containerId,
                        target.index,
                    );
                } else if (a.kind === "saved" && a.block) {
                    ok = insertNode(
                        draft,
                        cloneWithNewIds(a.block),
                        target.containerId,
                        target.index,
                    );
                } else if (a.kind === "node" && a.nodeId) {
                    ok = moveNode(draft, a.nodeId, target.containerId, target.index);
                }
                if (!ok) {
                    notify(`That ${dragLabel(a)} can’t be placed there.`, "error");
                }
            });
        },
        [applyDocChange, doc.root, notify, endDrag],
    );

    const rootChildren = doc.root.children ?? [];

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            onDragCancel={endDrag}
        >
            <BlockPalette savedBlocksVersion={savedBlocksVersion} />
            <div
                className="canvas-scroll"
                onClick={() => select(null)}
                data-testid="canvas"
            >
                <div
                    className="canvas-frame"
                    style={{
                        width: doc.settings.contentWidth,
                        backgroundColor: doc.settings.backgroundColor,
                    }}
                >
                    <RootDropArea rootId={doc.root.id}>
                        <SortableContext
                            items={rootChildren.map((c) => c.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {rootChildren.map((child) => (
                                <NodeRenderer
                                    key={child.id}
                                    node={child}
                                    parentId={doc.root.id}
                                    document={doc}
                                />
                            ))}
                        </SortableContext>
                        {rootChildren.length === 0 && (
                            <div className="canvas-empty">
                                Drag a section or block here to start building
                            </div>
                        )}
                    </RootDropArea>
                </div>
            </div>
            <DragOverlay dropAnimation={null}>
                {dragging && (
                    <div className="drag-ghost">
                        {dragging.kind === "palette"
                            ? paletteLabel(dragging.blockType ?? "")
                            : dragging.kind === "saved"
                              ? dragging.label
                              : dragging.nodeType}
                    </div>
                )}
            </DragOverlay>
            <Toaster />
        </DndContext>
    );
}

function Toaster() {
    const toast = useEditorStore((s) => s.toast);
    const dismiss = useEditorStore((s) => s.dismissToast);
    if (!toast) return null;
    return (
        <div
            className={`edm-toast edm-toast-${toast.kind}`}
            role="status"
            aria-live="polite"
            data-testid="editor-toast"
            onClick={dismiss}
        >
            {toast.message}
        </div>
    );
}

function RootDropArea({ rootId, children }: { rootId: string; children: React.ReactNode }) {
    const { setNodeRef, isOver } = useDroppable({
        id: `container:${rootId}`,
        data: { kind: "container", containerId: rootId },
    });
    return (
        <div ref={setNodeRef} className={`root-drop${isOver ? " over" : ""}`}>
            {children}
        </div>
    );
}
